import { SimEventKind } from "@highmarch/domain";
import { guide } from "../ui/guide/guide";
import type { GuideApi, GuideContext, GuideSignal } from "../ui/guide/guide";
import { motionOk } from "../ui/kit";
import { GuideEventSignals } from "./announcer";
import { LevelGuideWorld, TRAY_LINGER_MS, TroopFightWatch, WALKTHROUGH_DRAFT_LAST, levelBusy, levelFacts, trayQuiet, walkthroughSpeed } from "./guideLevel";
import type { LevelParts } from "./levelParts";
import { appHeight } from "../ui/appSize";

/** The guide's signals for what a player's command did, read from the events it raised. */
const COMMAND_SIGNALS: ReadonlyMap<SimEventKind, GuideSignal> = new Map<SimEventKind, GuideSignal>([
  [SimEventKind.CardPicked, "cardPicked"], [SimEventKind.CardUnpicked, "cardUnpicked"], [SimEventKind.SegmentPlaced, "roadPlaced"],
  [SimEventKind.TowerPlaced, "towerPlaced"], [SimEventKind.TowerUpgraded, "upgradeUsed"], [SimEventKind.OfferClosed, "offerClosed"],
]);

/**
 * The level as Captain Bram's host (DESIGN §4.3, §8): his world anchors, the facts and signals he reads, what keeps
 * him quiet, his hold on the sim and the level-1 walkthrough's ×1 speed.
 */
export class LevelGuideHost {
  /** Captain Bram's view of this level's world (level/guideLevel.ts); null with no guide installed. */
  private world: LevelGuideWorld | null = null;
  /** Signals raised since the guide's last update (the guide also hears each one as it happens). */
  private signals = new Set<GuideSignal>();
  /** Boss arrivals, wave starts and each archetype's first spawn, as guide signals. */
  private readonly events = new GuideEventSignals();
  private readonly fights = new TroopFightWatch();
  /** The level-1 walkthrough's ×1 while it is in force (DESIGN §7), else null. */
  private speedOverride: number | null = null;
  /** The player tapped the speed button this run: the walkthrough's ×1 is over for good. */
  private speedTapped = false;
  /**
   * The level is still being built (until its first frame): the guide waits. Its intro card is only set up at the end
   * of the build, and on a slow device the build outlasted the guide's quiet grace, so the welcome card showed, was
   * put aside for the intro card a moment later, then showed again: the same card twice.
   */
  private building = false;
  /** When the captain last had a line up (performance.now): the tray's hint waits TRAY_LINGER_MS after it. */
  private spokeAt = -Infinity;

  constructor(private readonly p: LevelParts) {}

  /** Raises a guide signal: the guide hears it now, and it rides with the next update's context. */
  readonly raise = (s: GuideSignal): void => {
    if (this.world === null) return;
    this.signals.add(s);
    guide()?.signal(s);
  };

  /** The player chose a speed: the walkthrough's ×1 is over for this run. */
  speedChosen(): void { this.speedTapped = true; }

  /** After a sim tick or a command: the guide's signals in its new events (from `from`), and the troops' fighting. */
  observe(command: boolean, from: number): void {
    if (this.world === null) return;
    const sim = this.p.sim;
    this.events.observe(sim, this.raise, from);
    this.fights.observe(sim);
    if (!command) return;
    const events = sim.events.all();
    for (let i = from; i < events.length; i++) {
      const s = COMMAND_SIGNALS.get(events[i].kind);
      if (s) this.raise(s);
    }
  }

  /** Hosts the guide on this level, if one is installed. */
  attach(): void {
    const g = guide();
    if (g === null) return;
    const p = this.p, rig = p.rig;
    this.world = new LevelGuideWorld({
      get sim() { return p.sim; },
      ghostIndex: () => p.taps.ghostIndex,
      towerSpot: () => p.taps.towerSpot,
      activeStep: () => guide()?.active ?? null,
      cellOnScreen: (c) => rig.cellOnScreen(c),
      enemyOnScreen: (id) => p.picker.enemyOnScreen(id),
      band: () => ({ top: p.layout.insetTop, bottom: appHeight() - p.layout.insetBottom }),
      glideTo: (c) => { if (motionOk()) rig.glideToCell(c); else rig.focusCell(c); },
    });
    this.building = true;
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => { this.building = false; }); else this.building = false;
    g.attach("level", {
      root: p.uiRoot,
      world: this.world,
      busy: () => this.building ? "loading" : levelBusy({ hud: p.hud.busyReason, tooltip: p.enemyCard.shown, settingsOpen: p.pause.state.sheetOpen, pausedLayer: p.pause.state.showsLayer }),
      hold: (on) => p.pause.guideHold(on),
      // M45: the level's own surface is its 3D view; a sheet or card over the middle of it puts a line aside
      screen: () => rig.renderer.domElement,
    });
  }

  /** The level is over or gone: the guide withdraws its step (not marked done) and lets go of any hold. Idempotent. */
  detach(): void {
    if (this.world === null) return;
    this.world = null;
    guide()?.detach("level");
    this.p.hand.setGuideQuiet(false);
  }

  /** New facts for the guide, once a frame and after every command; the tray keeps quiet while the walkthrough speaks. */
  update(): void {
    const g = guide();
    if (g === null || this.world === null) return;
    const signals = this.signals;
    this.signals = new Set();
    g.update(this.context(signals));
    const now = performance.now();
    if (g.active !== null) this.spokeAt = now;
    this.p.hand.setGuideQuiet(trayQuiet({
      level: this.p.sim.levelNumber, captainSpeaking: now - this.spokeAt < TRAY_LINGER_MS,
      walkthroughSkipped: g.walkthroughSkipped ?? false, draftTaught: g.isDone(WALKTHROUGH_DRAFT_LAST),
    }));
    this.syncSpeedOverride(g);
  }

  private context(signals: ReadonlySet<GuideSignal>): GuideContext {
    const p = this.p, sim = p.sim;
    const facts = levelFacts(sim, {
      towerSpotChosen: p.taps.towerSpot !== null, upgradeTargetChosen: p.upgrade.target >= 0, speed: p.hud.speed,
      troopsFighting: this.fights.fighting(sim), lastStand: p.moments.lastStand, towerSheetOpen: p.towerInfo.isOpen, rallyArmed: p.rally.armed,
    });
    // The level knows only its own record: a level not yet cleared means the one before it is the furthest cleared.
    return { screen: "level", level: facts, hub: null, end: null, highestCleared: sim.firstClear ? sim.levelNumber - 1 : sim.levelNumber, signals };
  }

  /** The walkthrough's ×1 (DESIGN §7): on while level 1's first clear teaches, off once the player picks a speed. */
  private syncSpeedOverride(g: GuideApi): void {
    const sim = this.p.sim;
    const want = walkthroughSpeed({
      level: sim.levelNumber, firstClear: sim.firstClear, guide: true, speedTaught: g.isDone("l1.speed"),
      walkthroughSkipped: g.walkthroughSkipped ?? false, tipsOff: g.tipsOff, speedTapped: this.speedTapped,
    });
    if (want === this.speedOverride) return;
    this.speedOverride = want;
    this.p.hud.setSpeedOverride(want);
    this.p.clock.speed = this.p.hud.speed;
  }
}
